import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { errMsg, useApi } from "@/lib/api";
import { fmtDate, usd } from "@/console/util";

export function ConsoleAudit() {
  const api = useApi();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/sa/audit").then((r) => setRows(r.data.rows)).catch((e) => toast.error(errMsg(e)));
  }, [api]);
  const LABEL = { demo_built: "Demo firm rebuilt", invite_created: "Invite created", invite_resent: "Invite email re-sent", temp_password_set: "Temporary password set", temp_password_invite_converted: "Invite turned into login (temporary password)" };
  if (!rows) return <p className="small-label">Loading…</p>;
  return (
    <div>
      <h1 className="font-serif text-4xl tracking-tight" data-testid="sa-audit-title">Audit log</h1>
      <p className="text-sm text-slate-500 mt-2">Every invite, temporary password and demo rebuild issued from this Console: who did it, when, and for whom. Passwords themselves are never stored.</p>
      <div className="panel mt-6 overflow-x-auto">
        <table className="w-full text-sm" data-testid="sa-audit-table">
          <thead><tr className="text-left small-label"><th className="p-3">When</th><th>Action</th><th>For</th><th>Firm</th><th>By</th><th>Detail</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-4 text-slate-400" data-testid="sa-audit-empty">Nothing recorded yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`sa-audit-row-${r.id}`}>
                <td className="p-3 whitespace-nowrap text-xs">{new Date(r.at).toLocaleString()}</td>
                <td className="whitespace-nowrap">{LABEL[r.action] || r.action}</td>
                <td>{r.target_email}</td>
                <td>{r.firm_name || "—"}</td>
                <td className="text-xs">{r.actor_email}</td>
                <td className="text-xs text-slate-500">{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ConsoleLogins() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [pw, setPw] = useState(null);
  const load = useCallback(() => api.get("/sa/users").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [api]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    if (!d) return [];
    const all = [...d.users.map((u) => ({ ...u, state: u.role === "owner" ? "superadmin" : u.tenant_id ? "active" : "no firm" })),
      ...d.invites.map((i) => ({ ...i, user_id: i.id, state: "invited" }))];
    return all.filter((r) => `${r.email} ${r.firm || ""}`.toLowerCase().includes(q.toLowerCase()));
  }, [d, q]);

  const tempPw = async (r) => {
    if (!window.confirm(`Set a new temporary password for ${r.email}? Their current password will stop working.`)) return;
    try {
      const res = await api.post(`/sa/users/${r.user_id}/temp-password`);
      setPw(res.data);
      if (res.data.converted_invite) load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!d) return <p className="small-label">Loading…</p>;
  return (
    <div>
      {pw && (
        <div className="panel p-5 mb-6 border-amber-400 bg-amber-50" data-testid="sa-temp-password-panel">
          <div className="small-label">Temporary password for {pw.email}</div>
          <div className="font-mono text-lg mt-2 select-all" data-testid="sa-temp-password-value">{pw.password}</div>
          <p className="text-xs text-slate-600 mt-2">Shown once. Give it to them privately; they can change it with “Forgot password?” on the sign-in page.</p>
          <button className="text-xs underline mt-2" onClick={() => setPw(null)} data-testid="sa-temp-password-dismiss">Dismiss</button>
        </div>
      )}
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Logins</h1>
      <p className="text-slate-500 mt-2 text-sm">Everyone who can sign in, across all firms. Manage a person's role or remove them from their firm's page.</p>
      <div className="relative w-full sm:w-80 mt-8">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or firm" className="pl-9 rounded-sm bg-white" data-testid="sa-logins-search-input" />
      </div>
      <div className="panel mt-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="sa-logins-table">
          <thead className="bg-slate-100"><tr className="small-label text-left"><th className="px-5 py-3">Email</th><th>Firm</th><th>Role</th><th>Status</th><th>Since</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-t border-slate-100">
                <td className="px-5 py-2.5">{r.email}</td>
                <td>{r.tenant_id ? <Link to={`/console/firms/${r.tenant_id}`} className="underline">{r.firm}</Link> : <span className="text-slate-400">—</span>}</td>
                <td className="text-xs font-mono uppercase">{r.role === "owner" ? "superadmin" : r.role}</td>
                <td className="text-xs font-mono uppercase text-slate-500">{r.state}</td>
                <td className="text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                <td className="pr-4 text-right">{r.state !== "superadmin" && (
                  <button className="text-xs underline text-slate-600 hover:text-slate-900" onClick={() => tempPw(r)} data-testid={`sa-temp-password-${r.user_id}`}>Set temporary password</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ConsolePayments() {
  const api = useApi();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/sa/payments").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api]);

  if (!rows) return <p className="small-label">Loading…</p>;
  return (
    <div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Payments</h1>
      <p className="text-slate-500 mt-2 text-sm">Subscription checkouts started by firm admins. Renewal invoices appear on each firm's page.</p>
      <div className="panel mt-8 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="sa-payments-table">
          <thead className="bg-slate-100"><tr className="small-label text-left"><th className="px-5 py-3">Date</th><th>Firm</th><th>By</th><th>Plan</th><th className="text-right">Amount</th><th className="pl-8 pr-5">Status</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-slate-400" data-testid="sa-payments-empty">No payments yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.session_id} className="border-t border-slate-100">
                <td className="px-5 py-2.5 text-xs">{fmtDate(r.created_at)}</td><td>{r.firm || "—"}</td><td className="text-xs">{r.email}</td>
                <td className="text-xs">{r.interval === "year" ? "Yearly" : "Monthly"}</td><td className="num text-right">{usd(r.amount)}</td>
                <td className="pl-8 pr-5 text-xs font-mono uppercase">{r.payment_status === "pending" && Date.now() - new Date(r.created_at).getTime() > 86400000 ? "abandoned" : r.payment_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
